import { useState, useEffect, useRef } from "react";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { useWebSocket } from "../../contexts/WebSocketContext";
import { useToast } from "../../hooks/use-toast";

export default function MessagesView() {
  const { user } = useAuth();
  const { subscribe, isConnected } = useWebSocket();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showNewConv, setShowNewConv] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // Store file for preview before sending
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeConvRef = useRef(null); // Keep track of active conversation for WebSocket
  const { toast } = useToast();

  // Keep activeConvRef in sync
  useEffect(() => {
    activeConvRef.current = activeConv;
  }, [activeConv]);

  useEffect(() => {
    fetchConversations();
    fetchContacts();
    
    // Subscribe to real-time chat messages
    const unsubscribe = subscribe("chat_message", (data) => {
      const message = data.data;
      
      // Add message to current conversation if it matches
      if (activeConvRef.current && message.conversation_id === activeConvRef.current.id) {
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }
      
      // Update conversation list with last message
      setConversations(prev => prev.map(c => 
        c.id === message.conversation_id 
          ? { ...c, last_message: message.content, last_message_at: message.created_at }
          : c
      ));
    });
    
    return () => unsubscribe();
  }, [subscribe]);

  // Handle a pre-filled relance coming from another view (e.g. Coach Radar)
  useEffect(() => {
    const raw = sessionStorage.getItem("odys-relance-prefill");
    if (!raw) return;
    sessionStorage.removeItem("odys-relance-prefill");
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    if (!payload?.studentId) return;

    (async () => {
      try {
        const res = await api.post("/messages/conversations", { other_user_id: payload.studentId });
        const convs = await api.get("/messages/conversations");
        setConversations(convs.data);
        const conv = convs.data.find(c => c.id === res.data.id);
        if (conv) {
          setActiveConv(conv);
          const msgs = await api.get(`/messages/conversations/${conv.id}/messages`);
          setMessages(msgs.data);
          if (payload.content) setInput(payload.content);
        }
      } catch {
        toast({ variant: "destructive", title: "Impossible d'ouvrir la conversation" });
      }
    })();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await api.get("/messages/conversations");
      setConversations(res.data);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      // Try to get contacts based on role
      const res = await api.get("/messages/contacts");
      setContacts(res.data.filter(u => u.id !== user?.id));
    } catch (e) {
      console.error("Failed to fetch contacts:", e);
      // Fallback for admin/coach
      if (user?.role === "admin" || user?.role === "coach") {
        try {
          const res = await api.get("/admin/users");
          setContacts(res.data.filter(u => u.id !== user?.id));
        } catch {
          try {
            const res = await api.get("/coach/students");
            setContacts(res.data);
          } catch (err) {
            console.warn("Could not load contacts:", err.message);
          }
        }
      }
    }
  };

  const handleOpenConversation = async (conv) => {
    setActiveConv(conv);
    try {
      const res = await api.get(`/messages/conversations/${conv.id}/messages`);
      setMessages(res.data);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleStartConversation = async (otherId) => {
    try {
      const res = await api.post("/messages/conversations", { other_user_id: otherId });
      setShowNewConv(false);
      await fetchConversations();
      const convs = await api.get("/messages/conversations");
      const newConv = convs.data.find(c => c.id === res.data.id);
      if (newConv) handleOpenConversation(newConv);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleDeleteMessage = async (msgId, e) => {
    if (e) e.stopPropagation();
    if (!activeConv) return;
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await api.delete(`/messages/conversations/${activeConv.id}/messages/${msgId}`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
      // Refresh conversation list for last-message preview
      fetchConversations();
      toast({ title: "Message supprimé" });
    } catch (e) {
      const status = e?.response?.status;
      toast({
        variant: "destructive",
        title: "Erreur de suppression",
        description: status === 403 ? "Vous ne pouvez supprimer que vos propres messages" : "Réessayez plus tard"
      });
    }
  };

  const handleDeleteConversation = async (convId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Supprimer cette conversation et tous ses messages ?\nCette action est irréversible.")) return;
    try {
      await api.delete(`/messages/conversations/${convId}`);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (activeConv?.id === convId) {
        setActiveConv(null);
        setMessages([]);
      }
      toast({ title: "Conversation supprimée" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur de suppression" });
    }
  };

  const handleSend = () => {
    handleSendWithAttachment();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeConv) return;
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({ variant: "destructive", title: "Fichier trop volumineux", description: "Maximum 10 Mo" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    
    // Store file for preview instead of sending immediately
    setPendingFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemovePendingFile = () => {
    setPendingFile(null);
  };

  const handleSendWithAttachment = async () => {
    if (!activeConv || sending || uploading) return;
    if (!input.trim() && !pendingFile) return;
    
    const content = input.trim();
    const file = pendingFile;
    
    setInput("");
    setPendingFile(null);
    
    // If there's a file, upload it first
    if (file) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        if (content) {
          formData.append("content", content);
        }
        
        const res = await api.post(`/messages/conversations/${activeConv.id}/attachment`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        setMessages(prev => [...prev, res.data]);
        const lastMsg = content ? `📎 ${file.name} - ${content}` : `📎 ${file.name}`;
        setConversations(prev => prev.map(c => c.id === activeConv.id ? { ...c, last_message: lastMsg } : c));
        toast({ title: "Message envoyé" });
      } catch (e) {
        console.error("Upload error:", e);
        toast({ variant: "destructive", title: "Erreur d'envoi" });
        setInput(content);
        setPendingFile(file);
      } finally {
        setUploading(false);
      }
    } else {
      // Just text, no file
      setSending(true);
      try {
        const res = await api.post(`/messages/conversations/${activeConv.id}/messages`, { content });
        setMessages(prev => [...prev, res.data]);
        setConversations(prev => prev.map(c => c.id === activeConv.id ? { ...c, last_message: content } : c));
      } catch (e) {
        toast({ variant: "destructive", title: "Erreur d'envoi" });
        setInput(content);
      } finally {
        setSending(false);
      }
    }
  };

  const renderAttachment = (attachment) => {
    if (!attachment) return null;
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(attachment.url);
    const fileName = attachment.filename || attachment.url.split("/").pop();
    
    if (isImage) {
      return (
        <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="block mt-2">
          <img src={attachment.url} alt={fileName} className="max-w-[200px] rounded-lg border border-gray-200" />
        </a>
      );
    }
    
    return (
      <a 
        href={attachment.url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 mt-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
      >
        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
        <span className="text-xs text-gray-700 truncate max-w-[150px]">{fileName}</span>
      </a>
    );
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  const formatDate = (ts) => {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleDateString("fr-FR");
    } catch { return ""; }
  };

  const roleColors = { admin: "from-amber-400 to-orange-400", coach: "from-blue-400 to-blue-500", student: "from-violet-400 to-fuchsia-400" };

  if (loading) return (
    <div className="p-6 space-y-3">
      {[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
    </div>
  );

  return (
    <div className="h-full flex" style={{ height: "calc(100vh - 65px)" }}>
      {/* Conversations List */}
      <div className="w-72 border-r border-slate-100 bg-white flex flex-col flex-shrink-0">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-0">
            <h3 className="font-semibold text-slate-800 text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>Messages</h3>
            <button data-testid="new-conversation-btn" onClick={() => setShowNewConv(!showNewConv)}
              className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          {showNewConv && contacts.length > 0 && (
            <div className="mt-3 bg-slate-50 rounded-xl p-2 space-y-1 max-h-48 overflow-y-auto">
              <p className="text-xs text-slate-400 px-2 pb-1">Nouvelle conversation</p>
              {contacts.map(c => (
                <button key={c.id} onClick={() => handleStartConversation(c.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white transition-colors text-left">
                  <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${roleColors[c.role] || "from-slate-300 to-slate-400"} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-xs font-bold">{c.first_name?.[0]}</span>
                  </div>
                  <span className="text-xs font-medium text-slate-700">{c.first_name} {c.last_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-slate-400">
              <p className="text-sm">Aucune conversation</p>
              <p className="text-xs mt-1">Commencez une nouvelle conversation</p>
            </div>
          ) : (
            conversations.map(c => (
              <div key={c.id} className="relative group">
                <button data-testid={`conversation-${c.id}`}
                  onClick={() => handleOpenConversation(c)}
                  className={`w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors border-b border-slate-50 text-left ${activeConv?.id === c.id ? "bg-violet-50 border-l-2 border-l-violet-400" : ""}`}>
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${roleColors[c.other_user_role] || "from-slate-300 to-slate-400"} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-xs font-bold">{c.other_user_name?.[0]}</span>
                  </div>
                  <div className="flex-1 min-w-0 pr-8">
                    <p className="text-sm font-medium text-slate-800 truncate">{c.other_user_name}</p>
                    <p className="text-xs text-slate-400 truncate">{c.last_message || "Pas encore de message"}</p>
                  </div>
                </button>
                <button
                  data-testid={`delete-conversation-${c.id}`}
                  onClick={(e) => handleDeleteConversation(c.id, e)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all"
                  title="Supprimer la conversation"
                >
                  <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-slate-50">
        {activeConv ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-slate-100 px-5 py-3.5 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${roleColors[activeConv.other_user_role] || "from-slate-300 to-slate-400"} flex items-center justify-center`}>
                <span className="text-white text-xs font-bold">{activeConv.other_user_name?.[0]}</span>
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">{activeConv.other_user_name}</p>
                <p className="text-xs text-slate-400">{activeConv.other_user_role}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-8">Commencez la conversation !</div>
              )}
              {messages.map((m, i) => {
                const isMe = m.sender_id === user?.id;
                const showDate = i === 0 || formatDate(messages[i-1]?.created_at) !== formatDate(m.created_at);
                return (
                  <div key={m.id}>
                    {showDate && (
                      <div className="text-center my-3">
                        <span className="text-xs text-slate-400 bg-white px-3 py-1 rounded-full border border-slate-100">{formatDate(m.created_at)}</span>
                      </div>
                    )}
                    <div className={`flex ${isMe ? "justify-end" : "justify-start"} group/msg items-center gap-2`} data-testid={`message-${m.id}`}>
                      {isMe && (
                        <button
                          data-testid={`delete-message-${m.id}`}
                          onClick={(e) => handleDeleteMessage(m.id, e)}
                          className="opacity-0 group-hover/msg:opacity-100 p-1.5 rounded-lg hover:bg-red-50 transition-all"
                          title="Supprimer ce message"
                        >
                          <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                          </svg>
                        </button>
                      )}
                      <div className={`max-w-xs lg:max-w-md ${isMe ? "bubble-user" : "bubble-ai"} px-4 py-2.5`}>
                        {m.content && <p className="text-sm leading-relaxed">{m.content}</p>}
                        {m.attachment && renderAttachment(m.attachment)}
                        <p className={`text-xs mt-1 ${isMe ? "text-white/60" : "text-slate-400"}`}>{formatTime(m.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-white border-t border-slate-100 p-4">
              {/* Pending file preview */}
              {pendingFile && (
                <div className="mb-3 p-3 bg-violet-50 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                      {pendingFile.type.startsWith('image/') ? (
                        <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-violet-800 truncate">{pendingFile.name}</p>
                      <p className="text-xs text-violet-500">{(pendingFile.size / 1024).toFixed(1)} Ko</p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemovePendingFile}
                    className="p-1.5 rounded-lg text-violet-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Retirer le fichier"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                />
                {/* Attachment button */}
                <button
                  data-testid="attach-file-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || pendingFile}
                  className="p-2.5 rounded-xl text-slate-400 hover:text-violet-500 hover:bg-violet-50 transition-all disabled:opacity-50"
                  title="Joindre un fichier"
                >
                  {uploading ? (
                    <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  )}
                </button>
                <input
                  data-testid="message-input"
                  type="text"
                  placeholder={pendingFile ? "Ajouter un message (optionnel)..." : "Écrire un message..."}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
                />
                <button
                  data-testid="send-message-btn"
                  onClick={handleSend}
                  disabled={(!input.trim() && !pendingFile) || sending || uploading}
                  className="p-2.5 rounded-xl text-white disabled:opacity-50 transition-all hover:scale-105"
                  style={{ background: "linear-gradient(135deg, #7c3aed, #d946ef)" }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-slate-400">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <p className="text-sm font-medium">Sélectionnez une conversation</p>
              <p className="text-xs mt-1">ou commencez-en une nouvelle</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
